import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { EnvelopeSimple, ChatTeardropText, MapPin, ArrowRight, CheckCircle } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import Footer from "@/components/Footer";

export default function Contact() {
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    // Simulate API call
    await new Promise(r => setTimeout(r, 1500));
    setBusy(false);
    setSent(true);
  };

  return (
    <div className="min-h-screen flex flex-col">
      <div className="flex-1 max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-32 grid grid-cols-12 gap-12 lg:gap-20">
        {/* Left: Info */}
        <div className="col-span-12 lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="eyebrow text-muted-foreground mb-4">Get in touch</div>
            <h1 className="font-serif text-5xl sm:text-7xl font-black tracking-tight leading-[0.95] mb-8">
              Letters to the<br/><span className="italic font-medium">editor.</span>
            </h1>
            <p className="text-lg text-muted-foreground leading-relaxed mb-12 max-w-md">
              Have a question about your account, a suggestion for the platform, or just want to say hello? 
              Our team reads every message.
            </p>

            <div className="space-y-8">
              <div className="flex items-start gap-4">
                <div className="bg-secondary p-3 rounded-none">
                  <EnvelopeSimple size={24} weight="duotone" />
                </div>
                <div>
                  <div className="font-serif text-xl font-bold">Email us</div>
                  <p className="text-sm text-muted-foreground mt-1">hello@inkspot.com</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="bg-secondary p-3 rounded-none">
                  <ChatTeardropText size={24} weight="duotone" />
                </div>
                <div>
                  <div className="font-serif text-xl font-bold">Support</div>
                  <p className="text-sm text-muted-foreground mt-1">Visit our <Link to="/help" className="link-underline">help center</Link> for FAQs.</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="bg-secondary p-3 rounded-none">
                  <MapPin size={24} weight="duotone" />
                </div>
                <div>
                  <div className="font-serif text-xl font-bold">Location</div>
                  <p className="text-sm text-muted-foreground mt-1">Distributed Team · Global</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Right: Form */}
        <div className="col-span-12 lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-card border border-border p-8 lg:p-12 shadow-sm"
          >
            {sent ? (
              <div className="py-12 text-center">
                <div className="flex justify-center mb-6">
                  <CheckCircle size={64} weight="duotone" className="text-emerald-600" />
                </div>
                <h2 className="font-serif text-3xl font-bold mb-4">Message received.</h2>
                <p className="text-muted-foreground mb-8">
                  Thank you for reaching out. We'll get back to you at the email provided within 24–48 hours.
                </p>
                <Button onClick={() => setSent(false)} variant="outline" className="rounded-none">
                  Send another message
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="name" className="eyebrow text-muted-foreground mb-2 block">Name</Label>
                    <Input id="name" placeholder="Your name" required className="rounded-none h-12" />
                  </div>
                  <div>
                    <Label htmlFor="email" className="eyebrow text-muted-foreground mb-2 block">Email</Label>
                    <Input id="email" type="email" placeholder="you@example.com" required className="rounded-none h-12" />
                  </div>
                </div>

                <div>
                  <Label htmlFor="subject" className="eyebrow text-muted-foreground mb-2 block">Subject</Label>
                  <Input id="subject" placeholder="What is this regarding?" required className="rounded-none h-12" />
                </div>

                <div>
                  <Label htmlFor="message" className="eyebrow text-muted-foreground mb-2 block">Message</Label>
                  <textarea
                    id="message"
                    required
                    rows={6}
                    placeholder="Your thoughts..."
                    className="w-full bg-background border border-input px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 rounded-none border-border"
                  />
                </div>

                <Button type="submit" disabled={busy} className="w-full h-12 rounded-none bg-foreground text-background hover:bg-foreground/90 text-sm font-bold uppercase tracking-widest">
                  {busy ? "Sending..." : (<>Send Message <ArrowRight size={16} weight="bold" className="ml-2" /></>)}
                </Button>
              </form>
            )}
          </motion.div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
